import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select as MuiSelect,
  Snackbar,
  Tab,
  Tabs,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import { createBrowserHistory } from "history";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select, { CSSObjectWithLabel, StylesConfig } from "react-select";

import {
  UPDATE_CALL_RECORD,
  UPDATE_HEALTHIE_FORM,
  UPDATE_SESSION_COUNT,
} from "@/pages/UserDetails/components/UsageHistory/queries";

// Create a history object
const history = createBrowserHistory();

// Define the type for form data
interface EmotionsData {
  responsiveBehaviours: string[];
  sentiments: string[];
}

// Update FormData interface to reflect new emotions structure
interface FormData {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scores?: any;
  notes?: string[];
  emotions?: EmotionsData;
  questionCount?: number;
}

interface HBAData {
  id: string;
  name: string;
  // questions: string[];
}

// HBA data and emotion options
const hbaData: HBAData[] = [
  { id: "hba1", name: "HBA 1" },
  { id: "hba2", name: "HBA 2" },
  { id: "hba3", name: "HBA 3" },
  { id: "hba4", name: "HBA 4" },
];

export interface Option {
  readonly value: string;
  readonly label: string;
  readonly color: string;
  readonly darkColor: string;
  readonly group: "responsiveBehaviours" | "sentiments";
}

export interface GroupedOption {
  readonly label: string;
  readonly options: readonly Option[];
}

const colorOpt1 = "#b4d9d8";
const colorOpt1Dark = "#9accca";
const colorOpt2 = "#beced7";
const colorOpt2Dark = "#a5bbc8";

const groupedEmotionOptions: readonly GroupedOption[] = [
  {
    label: "Response Behaviours",
    options: [
      {
        value: "immediate responses",
        label: "Immediate responses",
        color: colorOpt1,
        darkColor: colorOpt1Dark,
        group: "responsiveBehaviours",
      },
      {
        value: "impulsivity",
        label: "Impulsivity",
        color: colorOpt1,
        darkColor: colorOpt1Dark,
        group: "responsiveBehaviours",
      },
      {
        value: "negative commentary",
        label: "Negative commentary",
        color: colorOpt1,
        darkColor: colorOpt1Dark,
        group: "responsiveBehaviours",
      },
      {
        value: "perseveration",
        label: "Perseveration",
        color: colorOpt1,
        darkColor: colorOpt1Dark,
        group: "responsiveBehaviours",
      },
      {
        value: "positive commentary",
        label: "Positive commentary",
        color: colorOpt1,
        darkColor: colorOpt1Dark,
        group: "responsiveBehaviours",
      },
      {
        value: "verbal rehersal",
        label: "Verbal rehersal",
        color: colorOpt1,
        darkColor: colorOpt1Dark,
        group: "responsiveBehaviours",
      },
    ],
  },
  {
    label: "Sentiments",
    options: [
      {
        value: "agitated",
        label: "Agitated",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "anxious",
        label: "Anxious",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "bored",
        label: "Bored",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "confused",
        label: "Confused",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "deflated",
        label: "Deflated",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "discouraged",
        label: "Discouraged",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "distracted",
        label: "Distracted",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "encouraged",
        label: "Encouraged",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "engaged",
        label: "Engaged",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "excited",
        label: "Excited",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "fatigued",
        label: "Fatigued",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "frustrated",
        label: "Frustrated",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
      {
        value: "unengaged",
        label: "Unengaged",
        color: colorOpt2,
        darkColor: colorOpt2Dark,
        group: "sentiments",
      },
    ],
  },
];

const customStyle: StylesConfig<Option, true> = {
  menuPortal: (base) =>
    ({
      ...base,
      zIndex: 9999,
    } as CSSObjectWithLabel),

  control: (base) =>
    ({
      ...base,
      width: 425,
    } as CSSObjectWithLabel),

  option: (base, { data, isDisabled, isFocused, isSelected }) =>
    ({
      ...base,
      backgroundColor: isFocused ? data.darkColor : isSelected ? data.color : base.backgroundColor,
      cursor: isDisabled ? "not-allowed" : "default",
    } as CSSObjectWithLabel),

  multiValue: (base, { data }) =>
    ({
      ...base,
      backgroundColor: data.color || base.backgroundColor,
    } as CSSObjectWithLabel),

  group: (base) =>
    ({
      ...base,
      paddingTop: 8,
      paddingBottom: 8,
      "& > div:first-of-type": {
        fontWeight: "bold",
        color: "#666",
        paddingLeft: 12,
        marginBottom: 8,
      },
    } as CSSObjectWithLabel),
};

const HBATabs: React.FC<{
  sid?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loading: any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch: any;
}> = ({ sid, data, loading, refetch }) => {
  const [activeTab, setActiveTab] = useState<string>(hbaData[0].id);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("Data saved successfully.");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info" | "warning">(
    "success"
  );
  const [digitalSession, setDigitalSession] = useState<number | "">("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<Record<string, FormData>>({
    hba1: {
      scores: [],
      notes: [],
      emotions: { responsiveBehaviours: [], sentiments: [] },
      questionCount: 10,
    },
    hba2: {
      scores: [],
      notes: [],
      emotions: { responsiveBehaviours: [], sentiments: [] },
      questionCount: 10,
    },
    hba3: {
      scores: [],
      notes: [],
      emotions: { responsiveBehaviours: [], sentiments: [] },
      questionCount: 10,
    },
    hba4: {
      scores: [],
      notes: [],
      emotions: { responsiveBehaviours: [], sentiments: [] },
      questionCount: 10,
    },
  });
  // Add new state to track last pushed form data
  // const [lastPushedFormData, setLastPushedFormData] = useState<Record<string, FormData> | null>(null);
  const [currentForm, setCurrentForm] = useState<FormData>({
    scores: [],
    notes: [],
    emotions: { responsiveBehaviours: [], sentiments: [] },
    questionCount: 10,
  });
  const [unsavedTab, setUnsavedTab] = useState<string | null>(null);
  const [navigatingProgrammatically, setNavigatingProgrammatically] = useState<boolean>(false);
  // Add state to track if form has changed since last push
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasChangedSinceLastPush, setHasChangedSinceLastPush] = useState<boolean>(true);
  // Add state to track if any scores exist across all HBAs
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasAnyScores, setHasAnyScores] = useState<boolean>(false);
  const navigate = useNavigate();
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  const componentRef = useRef<HTMLDivElement>(null);

  const callRecordData = data;

  // Mutation to update call record
  const [updateCallRecord] = useMutation(UPDATE_CALL_RECORD);
  const [updateSessionCount] = useMutation(UPDATE_SESSION_COUNT);
  const [updateHealthieForm] = useMutation(UPDATE_HEALTHIE_FORM);

  useEffect(() => {
    if (callRecordData && callRecordData.callRecord) {
      const hbaDataArray = callRecordData.callRecord.hbacalldata || [];
      const parsedData = hbaDataArray.reduce(
        (acc: Record<string, FormData>, jsonString: string) => {
          try {
            const parsed = JSON.parse(jsonString);

            Object.keys(parsed).forEach((key) => {
              if (parsed[key].emotions && Array.isArray(parsed[key].emotions)) {
                const legacyEmotions = parsed[key].emotions;

                parsed[key].emotions = {
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  responsiveBehaviours: legacyEmotions.filter((emotion: any) =>
                    groupedEmotionOptions[0].options.some((opt) => opt.value === emotion)
                  ),
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  sentiments: legacyEmotions.filter((emotion: any) =>
                    groupedEmotionOptions[1].options.some((opt) => opt.value === emotion)
                  ),
                };
              }

              if (!parsed[key].notes || !Array.isArray(parsed[key].notes)) {
                parsed[key].notes = [];
              }
            });

            return { ...acc, ...parsed };
          } catch (error) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-console
            console.error("Failed to parse JSON string:", jsonString, error);
            return acc;
          }
        },
        {}
      );

      setFormData(parsedData);
      setCurrentForm(parsedData[activeTab] || { questionCount: 10, notes: [] });

      // Set the digital session from callRecord.sessioncount if it exists
      if (
        callRecordData.callRecord.sessioncount !== null &&
        callRecordData.callRecord.sessioncount !== undefined
      ) {
        setDigitalSession(Number(callRecordData.callRecord.sessioncount));
      }

      // If there's no lastPushedFormData yet, set it as the initial data
      // if (!lastPushedFormData) {
      //   setLastPushedFormData(parsedData);
      //   setHasChangedSinceLastPush(false);
      // }

      // Check if any scores exist across all HBAs
      checkForAnyScores(parsedData);
    }
  }, [callRecordData, activeTab]);

  useEffect(() => {
    if (callRecordData && callRecordData.callRecord) {
      setDigitalSession(callRecordData.callRecord.sessionCount);
    }
  }, [callRecordData]);

  // Check for any scores across all HBAs
  const checkForAnyScores = (data: Record<string, FormData>) => {
    const anyScores = Object.values(data).some(
      (hba) =>
        hba.scores &&
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hba.scores.some((score: any) => score !== null && score !== undefined)
    );
    setHasAnyScores(anyScores);
  };

  // Check if form data has changed compared to last pushed data
  // useEffect(() => {
  //   if (lastPushedFormData) {
  //     // Deep comparison of form data
  //     const isChanged = JSON.stringify(formData) !== JSON.stringify(lastPushedFormData);
  //     setHasChangedSinceLastPush(isChanged);
  //   }

  //   // Check if any scores exist across all HBAs
  //   checkForAnyScores(formData);
  // }, [formData, lastPushedFormData]);

  const handleSave = async (tabId: string) => {
    const currentQuestionCount = currentForm.questionCount || 10;
    const fullScores = Array(currentQuestionCount).fill(null);
    const fullNotes = Array(currentQuestionCount).fill("");

    // Copy existing scores, leaving others as null
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentForm.scores?.forEach((score: any, index: number) => {
      if (index < currentQuestionCount) {
        fullScores[index] = score;
      }
    });

    // Copy existing notes, leaving others as empty strings
    currentForm.notes?.forEach((note: string, index: number) => {
      if (index < currentQuestionCount) {
        fullNotes[index] = note;
      }
    });

    // Create updated form data for the current tab only
    const updatedTabData = {
      ...currentForm,
      scores: fullScores,
      notes: fullNotes,
      emotions: {
        responsiveBehaviours: currentForm.emotions?.responsiveBehaviours || [],
        sentiments: currentForm.emotions?.sentiments || [],
      },
    };

    // Create a new form data object that preserves all existing data
    // but updates only the current tab
    const newFormData = {
      ...formData,
      [tabId]: updatedTabData,
    };

    setFormData(newFormData);
    setUnsavedTab(null);

    try {
      // Create a complete data object with all HBA forms
      // ensuring scores are arrays of nulls with proper length
      const completeHbaData = {
        hba1: {
          questionCount: newFormData.hba1?.questionCount || 10,
          // Ensure we always have an array of the correct length with nulls
          scores:
            newFormData.hba1?.scores && newFormData.hba1.scores.length > 0
              ? newFormData.hba1.scores
              : Array(newFormData.hba1?.questionCount || 10).fill(null),
          notes:
            newFormData.hba1?.notes && newFormData.hba1.notes.length > 0
              ? newFormData.hba1.notes
              : Array(newFormData.hba1?.questionCount || 10).fill(""),
          emotions: newFormData.hba1?.emotions || {
            responsiveBehaviours: [],
            sentiments: [],
          },
        },
        hba2: {
          questionCount: newFormData.hba2?.questionCount || 10,
          // Ensure we always have an array of the correct length with nulls
          scores:
            newFormData.hba2?.scores && newFormData.hba2.scores.length > 0
              ? newFormData.hba2.scores
              : Array(newFormData.hba2?.questionCount || 10).fill(null),
          notes:
            newFormData.hba2?.notes && newFormData.hba2.notes.length > 0
              ? newFormData.hba2.notes
              : Array(newFormData.hba2?.questionCount || 10).fill(""),
          emotions: newFormData.hba2?.emotions || {
            responsiveBehaviours: [],
            sentiments: [],
          },
        },
        hba3: {
          questionCount: newFormData.hba3?.questionCount || 10,
          scores:
            newFormData.hba3?.scores && newFormData.hba3.scores.length > 0
              ? newFormData.hba3.scores
              : Array(newFormData.hba3?.questionCount || 10).fill(null),
          notes:
            newFormData.hba3?.notes && newFormData.hba3.notes.length > 0
              ? newFormData.hba3.notes
              : Array(newFormData.hba3?.questionCount || 10).fill(""),
          emotions: newFormData.hba3?.emotions || {
            responsiveBehaviours: [],
            sentiments: [],
          },
        },
        hba4: {
          questionCount: newFormData.hba4?.questionCount || 10,
          scores:
            newFormData.hba4?.scores && newFormData.hba4.scores.length > 0
              ? newFormData.hba4.scores
              : Array(newFormData.hba4?.questionCount || 10).fill(null),
          notes:
            newFormData.hba4?.notes && newFormData.hba4.notes.length > 0
              ? newFormData.hba4.notes
              : Array(newFormData.hba4?.questionCount || 10).fill(""),
          emotions: newFormData.hba4?.emotions || {
            responsiveBehaviours: [],
            sentiments: [],
          },
        },
      };

      // Check for any scores after save
      const anyScoresAfterSave = Object.values(completeHbaData).some(
        (hba) =>
          hba.scores &&
          hba.scores.some(
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (score: any) => score !== null && score !== undefined
          )
      );
      setHasAnyScores(anyScoresAfterSave);

      // Send the complete HBA data to the server
      await updateCallRecord({
        variables: {
          callSid: sid,
          data: completeHbaData,
        },
      });
      setAlertMessage("Data saved successfully.");
      setAlertSeverity("success");
      setShowAlert(true);
      refetch();
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Error saving data:", error);
      setAlertMessage("Error saving data. Please try again.");
      setAlertSeverity("error");
      setShowAlert(true);
    }
  };
  // Handle digital session change
  const handleDigitalSessionChange = async (value: number | "") => {
    setDigitalSession(value);

    // Update callRecord.sessioncount
    if (value !== "") {
      try {
        await updateSessionCount({
          variables: {
            callSid: sid,
            data: value,
          },
        });
        setAlertMessage("Session count updated successfully.");
        setAlertSeverity("success");
        setShowAlert(true);
        refetch();
      } catch (error) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-console
        console.error("Error updating session count:", error);
        setAlertMessage("Error updating session count. Please try again.");
        setAlertSeverity("error");
        setShowAlert(true);
      }
    }
  };

  const handleTabSwitch = async (tabId: string) => {
    if (unsavedTab && unsavedTab !== tabId) {
      // Determine the tab name to show in the confirmation message
      const tabNames: Record<string, string> = {
        hba1: "HBA-1",
        hba2: "HBA-2",
        hba3: "HBA-3",
        hba4: "HBA-4",
        // Add additional mappings if needed
      };

      // Get the display name for the unsaved tab
      const unsavedTabName = tabNames[unsavedTab] || "Unknown Tab";
      const newTabName = tabNames[tabId] || "Unknown Tab";

      // Show confirmation message with specific tab names
      const userConfirmed = window.confirm(
        `You have unsaved changes in ${unsavedTabName}. Do you want to save changes before switching to ${newTabName}?`
      );

      if (userConfirmed) {
        await handleSave(unsavedTab);
      } else {
        // If the user discards changes, reset the form for the new tab
        setCurrentForm(formData[tabId] || { questionCount: 10 });
      }
    } else {
      // If no unsaved changes or the same tab is selected, just update the form
      setCurrentForm(formData[tabId] || { questionCount: 10 });
    }

    // Update the active tab
    setActiveTab(tabId);
  };

  const handleScoreChange = (hbaId: string, questionIndex: number, score: number) => {
    setUnsavedTab(hbaId);
    setCurrentForm((prev) => {
      const updatedScores = [...(prev.scores || [])];
      // Explicitly set unanswered questions to null
      while (updatedScores.length < questionIndex) {
        updatedScores.push(null);
      }
      updatedScores[questionIndex] = score;
      return {
        ...prev,
        scores: updatedScores,
      };
    });
  };

  const handleNoteChange = (hbaId: string, questionIndex: number, note: string) => {
    setUnsavedTab(hbaId);
    setCurrentForm((prev) => {
      const updatedData = { ...prev };
      if (!updatedData.notes) {
        updatedData.notes = [];
      }
      updatedData.notes[questionIndex] = note;
      return updatedData;
    });
  };

  const handleEmotionChange = (hbaId: string, selectedOptions: Option[]) => {
    setUnsavedTab(hbaId);
    const groupedEmotions = selectedOptions.reduce(
      (acc, option) => {
        if (option.group === "responsiveBehaviours") {
          acc.responsiveBehaviours.push(option.value);
        } else {
          acc.sentiments.push(option.value);
        }
        return acc;
      },
      { responsiveBehaviours: [], sentiments: [] } as EmotionsData
    );

    setCurrentForm((prev) => ({
      ...prev,
      emotions: groupedEmotions,
    }));
  };

  const calculateFinalScore = (hbaId: string): string => {
    const hba = formData[hbaId] || { scores: [], questionCount: 10 };
    const questionCount = hba.questionCount ?? 10;

    const fullScoresArray = Array(questionCount).fill(null);

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hba.scores?.forEach((score: any, index: number) => {
      if (index < questionCount) {
        fullScoresArray[index] = score;
      }
    });

    const answeredScores = fullScoresArray.filter((score) => score !== null && score !== undefined);
    const totalScore = answeredScores.reduce((acc, score) => acc + (score || 0), 0);
    const percentage = answeredScores?.length > 0 ? (totalScore / answeredScores?.length) * 100 : 0;

    return percentage.toFixed(2);
  };

  const handleDiscard = () => {
    if (callRecordData && callRecordData.callRecord) {
      const hbaDataArray = callRecordData.callRecord.hbacalldata || [];
      const parsedData = hbaDataArray.reduce(
        (acc: Record<string, FormData>, jsonString: string) => {
          try {
            const parsed = JSON.parse(jsonString);
            return { ...acc, ...parsed };
          } catch (error) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-console
            console.error("Failed to parse JSON string:", jsonString, error);
            return acc;
          }
        },
        {}
      );

      setFormData(parsedData);
      setCurrentForm(
        parsedData[activeTab] || {
          scores: [],
          notes: [],
          emotions: { responsiveBehaviours: [], sentiments: [] },
        }
      );
    }
  };

  const handleNavigation = (path: string) => {
    if (unsavedTab) {
      const userConfirmed = window.confirm(
        "You have unsaved changses. Do you want to save before leaving?"
      );
      if (userConfirmed) {
        handleSave(unsavedTab);
      }
      navigate(path);
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    if (navigatingProgrammatically) {
      handleNavigation("/activity-library"); // Redirect to a specific path or use dynamic path
      setNavigatingProgrammatically(false);
    }
  }, [navigatingProgrammatically]);

  useEffect(() => {
    // Function to handle browser tab/window close or refresh
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleBeforeUnload = (event: any) => {
      if (unsavedTab) {
        event.preventDefault();
        event.returnValue = ""; // Show the browser's default confirmation dialog
      }
    };

    // Add event listener for the beforeunload event
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Cleanup the event listener
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [unsavedTab]);

  useEffect(() => {
    // Function to handle in-app navigation
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleNavigation = (location: any) => {
      if (unsavedTab) {
        const userConfirmed = window.confirm(
          "You have unsaved changes. Do you want to save before navigating to a different page?"
        );

        if (userConfirmed) {
          handleSave(unsavedTab).then(() => {
            navigate(location.pathname); // Continue navigation after save
          });
        } else {
          // Allow navigation without saving
          navigate(location.pathname);
        }
      } else {
        // Allow navigation without prompt
        navigate(location.pathname);
      }
    };

    // Listen to changes in route using history.listen
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unlisten = history.listen((location: any) => {
      if (location.pathname !== location.pathname) {
        handleNavigation(location);
      }
    });

    return () => {
      unlisten(); // Cleanup the listener when the component unmounts
    };
  }, [unsavedTab, handleSave, navigate]);

  const handleQuestionCountChange = (hbaId: string, newCount: number) => {
    setUnsavedTab(hbaId);
    setCurrentForm((prev) => {
      // Create a copy of the current scores and notes arrays
      const currentScores = [...(prev.scores || [])];
      const currentNotes = [...(prev.notes || [])];

      // If reducing the count, truncate both arrays
      if (newCount < (prev.questionCount || 10)) {
        return {
          ...prev,
          questionCount: newCount,
          scores: currentScores.slice(0, newCount),
          notes: currentNotes.slice(0, newCount), // Truncate notes to match new count
        };
      }

      return {
        ...prev,
        questionCount: newCount,
      };
    });
  };

  useEffect(() => {
    // Recalculate the final score when question count or score changes
    calculateFinalScore(activeTab);
  }, [
    handleScoreChange,
    handleQuestionCountChange,
    activeTab,
    currentForm.questionCount,
    currentForm.scores,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        if (unsavedTab) {
          const userConfirmed = window.confirm(
            "You have unsaved changes. Do you want to save before leaving?"
          );
          if (userConfirmed) {
            handleSave(unsavedTab); // Call save with latest data
          } else {
            handleDiscard(); // Handle discarding the unsaved changes
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [unsavedTab, currentForm]); //

  const getAllEmotionOptions = () => {
    return groupedEmotionOptions.reduce((acc: Option[], group) => [...acc, ...group.options], []);
  };

  const getSelectedOptions = () => {
    if (!currentForm.emotions) {
      return [];
    }

    const legacyEmotions = Array.isArray(currentForm.emotions) ? currentForm.emotions : [];

    const groupedEmotions = currentForm.emotions as EmotionsData;

    const allEmotions = [
      ...(groupedEmotions.responsiveBehaviours || []),
      ...(groupedEmotions.sentiments || []),
      ...legacyEmotions,
    ];

    return getAllEmotionOptions().filter((option) => allEmotions.includes(option.value));
  };

  // Check if there are any scores entered in the current tab
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hasEnteredScores = () => {
    return (
      currentForm.scores &&
      currentForm.scores.some(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (score: any) => score !== null && score !== undefined
      )
    );
  };

  // Handle pushing data to TEN
  const handlePushToTen = async () => {
    if (!data?.callRecord?.account?.Id_Ehr) {
      setAlertMessage(
        "This patient does not have an EHR Id. Please fill this out before you proceed with pushing the scores."
      );
      setAlertSeverity("error");
      setShowAlert(true);
      return;
    }

    if (digitalSession === "") {
      setAlertMessage("Please select a digital session number before pushing data.");
      setAlertSeverity("error");
      setShowAlert(true);
      return;
    }

    if (unsavedTab) {
      const userConfirmed = window.confirm(
        "You have unsaved changes. Do you want to save before pushing data to TEN?"
      );
      if (userConfirmed) {
        await handleSave(unsavedTab);
      } else {
        return;
      }
    }

    const formDataLatest = { ...formData, [activeTab]: currentForm };
    const anyHbaHasScores = Object.values(formDataLatest).some((hba: FormData | undefined) =>
      hba?.scores?.some((s: number | null | undefined) => s != null)
    );

    if (!anyHbaHasScores) {
      setAlertMessage(
        "Please enter at least one score in any HBA form before pushing data to TEN."
      );
      setAlertSeverity("error");
      setShowAlert(true);
      return;
    }

    setIsLoading(true);

    try {
      const mainData = callRecordData.callRecord;

      const healthieData = {
        sid: mainData?.sid,
        createdOn: mainData?.createdOn,
        programRating: mainData?.programRating,
        reflectionOnLearnings: mainData?.reflectionOnLearnings,
        fullProgramRatingResponse: mainData?.fullProgramRatingResponse,
        performanceRating: mainData?.performanceRating,
        fullPerformanceRatingResponse: mainData?.fullPerformanceRatingResponse,
        hbacalldata: mainData?.hbacalldata,
        sessionCount: mainData?.sessionCount,
        digitalSession,
        playlist: mainData?.playlist
          ? Array.isArray(mainData.playlist)
            ? // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              mainData.playlist.map((playlistItem: any) => ({
                id: playlistItem?.id,
                isActive: playlistItem?.isActive,
                startOn: playlistItem?.startOn,
                endOn: playlistItem?.endOn,
                title: playlistItem?.title,
                playlistVoiceMessages: playlistItem?.playlistVoiceMessages?.map(
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (voiceMessage: any) => ({
                    title: voiceMessage?.voiceMessage?.title,
                    templateLink: voiceMessage?.voiceMessage?.templateLink,
                    feature: voiceMessage?.voiceMessage?.feature,
                    cognitiveDomains: voiceMessage?.voiceMessage?.cognitiveDomains,
                  })
                ),
              }))
            : [
                {
                  id: mainData.playlist?.id,
                  isActive: mainData.playlist?.isActive,
                  startOn: mainData.playlist?.startOn,
                  endOn: mainData.playlist?.endOn,
                  title: mainData.playlist?.title,
                  playlistVoiceMessages: mainData.playlist?.playlistVoiceMessages?.map(
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (voiceMessage: any) => ({
                      title: voiceMessage?.voiceMessage?.title,
                      templateLink: voiceMessage?.voiceMessage?.templateLink,
                      feature: voiceMessage?.voiceMessage?.feature,
                      cognitiveDomains: voiceMessage?.voiceMessage?.cognitiveDomains,
                    })
                  ),
                },
              ]
          : [],
        account: {
          firstName: mainData?.account?.firstName,
          lastName: mainData?.account?.lastName,
          id: mainData?.account?.id,
          Id_Ehr: mainData?.account?.Id_Ehr,
        },
      };

      const result = await updateHealthieForm({
        variables: {
          callSid: mainData?.sid,
          data: healthieData,
        },
      });

      await refetch();

      const response = result.data?.updateHealthieForm;
      const responseStatus = response?.status;
      let responseMessage = response?.message;

      // ✅ Safely parse message if it's a JSON string
      try {
        const parsed = JSON.parse(responseMessage);
        if (parsed?.status) {
          responseMessage = parsed.status;
        }
      } catch {
        // Fallback to raw string if not JSON
      }

      // ✅ Clean unwanted characters
      responseMessage = responseMessage?.replace(/<|>/g, "");

      // ✅ Message handling logic
      const HBA_SUCCESS_MSG = "HBA Data Processing Complete Private Notes Retrieved";
      const TEST_EHR_ERROR =
        "Request received in test environment and the healthie id is not approved to test";
      const CHART_ERROR = "Chart Values Processing Error";

      if (responseMessage.includes(HBA_SUCCESS_MSG)) {
        responseMessage = "HBA scores were successfully pushed to the corresponding TEN!";
        setAlertSeverity("success");
      } else if (responseMessage.includes(TEST_EHR_ERROR)) {
        responseMessage =
          "We could not find a patient in Healthie that matches the EHR ID provided. Please make sure the EHR ID listed for this patient is correct before you proceed.";
        setAlertSeverity("error");
      } else if (responseMessage.includes(CHART_ERROR)) {
        responseMessage =
          "Patient does not have a TEN form that matches the start and end dates for this week's playlist. Please make sure to add a TEN and/or ensure it has the right start and end dates before you proceed.";
        setAlertSeverity("error");
      } else {
        setAlertSeverity(responseStatus || "info");
      }

      setAlertMessage(responseMessage);
      setShowAlert(true);
    } catch (error) {
      setAlertMessage(error instanceof Error ? error.message : "Unexpected error occurred");
      setAlertSeverity("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Create a helper function to check if any HBA has scores
  const anyHbaHasScores = () => {
    return Object.keys(formData).some((hbaKey) => {
      const hba = formData[hbaKey];
      return (
        hba?.scores &&
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hba.scores.some((score: any) => score !== null && score !== undefined)
      );
    });
  };

  return (
    <>
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel id="digital-session-label">Digital Session</InputLabel>
          <MuiSelect
            labelId="digital-session-label"
            value={digitalSession}
            onChange={(e) => handleDigitalSessionChange(e.target.value as number)}
            label="Digital Session"
          >
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={3}>3</MenuItem>
          </MuiSelect>
        </FormControl>

        {isLoading ? (
          <CircularProgress />
        ) : (
          <Button
            variant="outlined"
            onClick={handlePushToTen}
            style={{
              background: "#000",
              color: "#fff",
              opacity:
                !data?.callRecord?.account?.Id_Ehr ||
                digitalSession === "" ||
                digitalSession === null ||
                !anyHbaHasScores()
                  ? // ||
                    // !hasChangedSinceLastPush
                    "0.5"
                  : "1",
              cursor:
                !data?.callRecord?.account?.Id_Ehr ||
                digitalSession === "" ||
                digitalSession === null ||
                !anyHbaHasScores()
                  ? // ||
                    // !hasChangedSinceLastPush
                    "not-allowed"
                  : "",
            }}
            disabled={
              !data?.callRecord?.account?.Id_Ehr ||
              digitalSession === "" ||
              digitalSession === null ||
              !anyHbaHasScores() ||
              // !hasChangedSinceLastPush ||
              isLoading
            }
          >
            Push to TEN
          </Button>
        )}
      </Box>
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: "grid",
          alignItems: "center",
          gap: 2,
        }}
      >
        {!data?.callRecord?.account?.Id_Ehr && (
          <Typography color="error" variant="body2">
            This patient does not have an EHR Id. Please fill this out before you proceed with
            pushing the scores.
          </Typography>
        )}
        {!anyHbaHasScores() && (
          <Typography color="error" variant="body2">
            Please score the HBAs before you proceed.
          </Typography>
        )}
        {/* {!hasChangedSinceLastPush && anyHbaHasScores() && (
        <Typography color="info" variant="body2">
          No changes detected since the last push to TEN. Make changes to enable pushing data again.
        </Typography>
      )} */}
      </Box>
      <div ref={componentRef}>
        <Tabs
          value={activeTab}
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onChange={(e, newValue) => handleTabSwitch(newValue)}
          style={{ overflow: "scroll" }}
        >
          {hbaData.map((hba) => (
            <Tab
              key={hba.id}
              value={hba.id}
              label={
                <div>
                  {hba.name} ({calculateFinalScore(hba.id)}%)
                </div>
              }
            />
          ))}
        </Tabs>
        <div
          style={{
            background: "#fef6e5",
            padding: "20px",
            margin: "0",
          }}
        >
          <h3 style={{ textTransform: "uppercase" }}>
            {activeTab === "hba1"
              ? "HBA 1"
              : activeTab === "hba2"
              ? "HBA 2"
              : activeTab === "hba3"
              ? "HBA 3"
              : activeTab === "hba4"
              ? "HBA 4"
              : ""}{" "}
            Score: {calculateFinalScore(activeTab)}%{loading ? <div>Loading New data</div> : ""}
          </h3>

          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <span>Total Questions</span>

            <Button
              onClick={() =>
                handleQuestionCountChange(activeTab, (currentForm.questionCount || 10) + 1)
              }
              variant="outlined"
              size="small"
              style={{ marginRight: "5px" }}
            >
              +
            </Button>
            <div>{currentForm.questionCount}</div>
            <Button
              onClick={() =>
                handleQuestionCountChange(
                  activeTab,
                  Math.max((currentForm.questionCount || 10) - 1, 1)
                )
              }
              variant="outlined"
              size="small"
              style={{ marginRight: "5px" }}
            >
              -
            </Button>
          </div>
          <div style={{ margin: "20px 0 10px 0" }}>
            <Select
              placeholder="Select Sentiments"
              options={groupedEmotionOptions}
              styles={customStyle}
              isMulti
              value={getSelectedOptions()}
              onChange={(selectedOptions) =>
                handleEmotionChange(activeTab, selectedOptions as Option[])
              }
              isClearable={false}
            />
          </div>
        </div>
        <div
          style={{
            background: "#fffaf2",
          }}
        >
          {Array.from({ length: currentForm.questionCount || 10 }).map((_, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                gap: "20px",
                width: "100%",
                padding: "20px",
                alignItems: "center",
                background: index % 2 === 0 ? "#fffaf2" : "#fef6e5", // Alternating background
              }}
            >
              <span
                style={{
                  width: "100%",
                  maxWidth: "max-content",
                }}
              >
                Question {index + 1}
              </span>

              <RadioGroup
                style={{ width: "100%", maxWidth: "max-content" }}
                row
                value={currentForm.scores?.[index] || ""}
                onChange={(e) => handleScoreChange(activeTab, index, Number(e.target.value))}
              >
                <FormControlLabel
                  control={
                    <Radio
                      checked={currentForm.scores?.[index] === 0}
                      style={{ color: "#f40012", zIndex: "0" }}
                    />
                  }
                  label="0"
                  value="0.0"
                />
                <FormControlLabel
                  value={0.5}
                  checked={currentForm.scores?.[index] === 0.5}
                  control={<Radio style={{ color: "#ffa500", zIndex: "0" }} />}
                  label="0.5"
                />
                <FormControlLabel
                  checked={currentForm.scores?.[index] === 1}
                  value={1}
                  control={<Radio style={{ color: "green", zIndex: "0" }} />}
                  label="1.0"
                />
              </RadioGroup>
              <TextareaAutosize
                style={{ width: "100%", padding: "10px" }}
                minRows={3}
                placeholder="Add notes"
                value={currentForm.notes?.[index] || ""}
                onChange={(e) => handleNoteChange(activeTab, index, e.target.value)}
              />
            </div>
          ))}

          <Button
            onClick={() => handleSave(activeTab)}
            variant="contained"
            color="primary"
            style={{ margin: "20px" }}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              if (unsavedTab) {
                const tabNames: Record<string, string> = {
                  hba1: "HBA-1",
                  hba2: "HBA-2",
                  hba3: "HBA-3",
                  hba4: "HBA-4",
                  // Add additional mappings if needed
                };

                const unsavedTabName = tabNames[unsavedTab] || "Unknown Tab";
                const userConfirmed = window.confirm(
                  `You have unsaved changes in ${unsavedTabName}. Are you sure you want to discard changes?`
                );
                if (userConfirmed) {
                  setUnsavedTab(null);
                  handleDiscard();
                }
              }
            }}
          >
            Discard
          </Button>
        </div>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showAlert}
        onClose={() => setShowAlert(false)}
        autoHideDuration={2000}
      >
        <Alert onClose={() => setShowAlert(false)} severity={alertSeverity}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HBATabs;
